import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useAuth } from '../../../AuthContext';
import './PersonalBestForm.css';

const PersonalBestForm = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [personalBest, setPersonalBest] = useState({
    time1bak: '',
    time3bak: ''
  });
  
  // Refs for input fields
  const time1bakRef = useRef(null);
  const time3bakRef = useRef(null);
  const submitButtonRef = useRef(null);

  // Fetch user's personal best times if they exist
  useEffect(() => {
    const fetchPersonalBest = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        const userDocRef = doc(db, 'userPersonalBests', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setPersonalBest({
            time1bak: userData.time1bak || '',
            time3bak: userData.time3bak || ''
          });
        }
      } catch (err) {
        console.error('Error fetching personal best:', err);
        setError('Failed to load your personal best times.');
      } finally {
        setLoading(false);
      }
    };

    fetchPersonalBest();
  }, [currentUser]);

  // Add keyboard event listener for Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Escape key to dismiss messages
      if (e.key === 'Escape') {
        setMessage('');
        setError('');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Format time input as user types
  const formatTimeInput = (value) => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, '');
    
    if (digitsOnly.length <= 2) {
      return digitsOnly;
    } else {
      // Format as MM:SS
      return `${digitsOnly.slice(0, 2)}:${digitsOnly.slice(2, 4)}`;
    }
  };

  // Handle form input changes with formatting
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Format the time input
    const formattedValue = formatTimeInput(value);
    
    setPersonalBest({
      ...personalBest,
      [name]: formattedValue
    });
  };

  // Handle key press in input fields
  const handleKeyDown = (e, nextFieldRef) => {
    // If Enter is pressed, move to next field or submit
    if (e.key === 'Enter') {
      e.preventDefault();
      if (nextFieldRef) {
        nextFieldRef.current.focus();
      } else {
        // If no next field, submit the form
        handleSubmit(e);
      }
    }
  };

  // Save personal best times
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser) {
      setError('You must be logged in to save your personal best times.');
      return;
    }
    
    try {
      setLoading(true);
      setMessage('');
      setError('');
      
      // Validate time format (optional)
      const timeRegex = /^\d{2}:\d{2}$/;
      if (personalBest.time1bak && !timeRegex.test(personalBest.time1bak)) {
        setError('1BAK time should be in format --:--');
        setLoading(false);
        time1bakRef.current.focus();
        return;
      }
      
      if (personalBest.time3bak && !timeRegex.test(personalBest.time3bak)) {
        setError('3BAK time should be in format --:--');
        setLoading(false);
        time3bakRef.current.focus();
        return;
      }
      
      // Save to Firestore
      const userDocRef = doc(db, 'userPersonalBests', currentUser.uid);
      await setDoc(userDocRef, {
        userId: currentUser.uid,
        email: currentUser.email,
        displayName: currentUser.displayName || '',
        time1bak: personalBest.time1bak || '--:--',
        time3bak: personalBest.time3bak || '--:--',
        updatedAt: new Date()
      }, { merge: true });
      
      setMessage('Your personal best times have been saved!');
      
      // Clear focus from inputs after successful submission
      if (document.activeElement) {
        document.activeElement.blur();
      }
    } catch (err) {
      console.error('Error saving personal best:', err);
      setError('Failed to save your personal best times. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Dismiss messages when clicked
  const dismissMessage = () => {
    setMessage('');
    setError('');
  };

  if (!currentUser) {
    return (
      <div className="personal-best-container">
        <p className="login-message">Please log in to record your personal best times.</p>
      </div>
    );
  }

  return (
    <div className="personal-best-container">
      <h3>Vul hier jouw snelste baktijd in!</h3>
      
      {error && (
        <div 
          className="personal-best-error" 
          onClick={dismissMessage}
          role="alert"
          tabIndex={0}
          aria-label="Error message, click or press Escape to dismiss"
        >
          {error}
        </div>
      )}
      
      {message && (
        <div 
          className="personal-best-success" 
          onClick={dismissMessage}
          role="status"
          tabIndex={0}
          aria-label="Success message, click or press Escape to dismiss"
        >
          {message}
        </div>
      )}
      
      <form className="personal-best-form" onSubmit={handleSubmit}>
        <div className="personal-best-inputs">
          <div className="form-group">
            <label htmlFor="time1bak">1BAK</label>
            <input
              type="text"
              id="time1bak"
              name="time1bak"
              ref={time1bakRef}
              value={personalBest.time1bak}
              onChange={handleInputChange}
              onKeyDown={(e) => handleKeyDown(e, time3bakRef)}
              placeholder="--:--"
              maxLength={5}
              aria-label="Enter your 1BAK time in format MM:SS"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="time3bak">3BAK</label>
            <input
              type="text"
              id="time3bak"
              name="time3bak"
              ref={time3bakRef}
              value={personalBest.time3bak}
              onChange={handleInputChange}
              onKeyDown={(e) => handleKeyDown(e, submitButtonRef)}
              placeholder="--:--"
              maxLength={5}
              aria-label="Enter your 3BAK time in format MM:SS"
            />
          </div>
        </div>
        
        <button 
          type="submit" 
          className="personal-best-submit"
          ref={submitButtonRef}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Tijden Opslaan'}
        </button>
      </form>
    </div>
  );
};

export default PersonalBestForm;